import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);
const DEFAULTS = { system_name: "OS Flow", company_name: "", primary_color: "#059669", font: "Plus Jakarta Sans", logo_url: null, fonts: [] };
const BASE = process.env.REACT_APP_BACKEND_URL;

const shade = (hex, pct) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (pct < 0 ? c * pct : (255 - c) * pct))));
  const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export function applyBranding(b) {
  const root = document.documentElement;
  const c = b.primary_color || DEFAULTS.primary_color;
  root.style.setProperty("--brand", c);
  root.style.setProperty("--brand-dark", shade(c, -0.15));
  root.style.setProperty("--brand-light", shade(c, 0.12));
  root.style.setProperty("--brand-font", `"${b.font || DEFAULTS.font}", sans-serif`);
  document.title = b.system_name || DEFAULTS.system_name;
  const v = encodeURIComponent(b.updated_at || "");
  const setHref = (sel, href) => { const el = document.querySelector(sel); if (el) el.href = href; };
  setHref('link[rel="apple-touch-icon"]', `/api/pwa/icon/180.png?v=${v}`);
  setHref('link[rel="icon"]', `/api/pwa/icon/192.png?v=${v}`);
  setHref('link[rel="manifest"]', `/api/pwa/manifest.webmanifest?v=${v}`);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", c);
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", b.system_name || DEFAULTS.system_name);
  const id = "brand-font-link";
  let link = document.getElementById(id);
  if (!link) { link = document.createElement("link"); link.id = id; link.rel = "stylesheet"; document.head.appendChild(link); }
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(b.font || DEFAULTS.font).replace(/%20/g, "+")}:wght@400;500;600;700;800&display=swap`;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("os_branding") || "{}") }; } catch { return DEFAULTS; }
  });
  const set = useCallback((b) => {
    const merged = { ...DEFAULTS, ...b, logo_url: b.logo_url ? `${BASE}${b.logo_url}` : null };
    setBranding(merged); localStorage.setItem("os_branding", JSON.stringify(merged)); applyBranding(merged);
  }, []);
  const reload = useCallback(async () => {
    try { const r = await api.get("/branding/public"); set(r.data); } catch {}
  }, [set]);
  useEffect(() => { applyBranding(branding); reload(); }, []); // eslint-disable-line
  return <BrandingContext.Provider value={{ branding, reload, set }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
